import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, Injector, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { REQUEST } from '../../../express.tokens';
import { environment } from '../../../environments/environment';
import { PlatformService } from 'ngx-unificator/services';
import { ignoreHosts } from '../../../config/ignore.hosts';

@Injectable({
  providedIn: 'root',
})
export class HostRequestsInterceptor implements HttpInterceptor {

  private _platform = inject(PlatformService);
  private _injector = inject(Injector);

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const appUrl = `https://${this._getAppHost()}`;

    if (this._platform.isBrowser) {
      req = req.clone({
        url: req.params.has('crossdomain') ? req.url : this.overrideUrl(req.url, appUrl),
      });
      return next.handle(req);
    } else {
      req = req.clone({
        url: this.overrideUrl(req.url, appUrl).replace('https://', 'http://'),
        setHeaders: this._injector.get(REQUEST).headers,
      });
      return next.handle(req);
    }

  }

  public getDomainName() {
    return this._getAppHost();
  }

  /**
   * Change provided url to depending on host url
   *
   * @param url
   * @param appUrl
   */
  public overrideUrl(url: string, appUrl: string = `https://${this._getAppHost()}`): string {
    switch (true) {
      case this._isIgnoredHostSS(appUrl) && this._isIgnoredHostCMS(appUrl):
        return url;
      case this._isIgnoredHostCMS(appUrl):
        return this._overrideSSHost(url, appUrl);
      case this._isIgnoredHostSS(appUrl):
        return this._overrideCmsHost(url, appUrl);
      case this._isRequestToCms(url):
        return this._overrideCmsHost(url, appUrl);
      case this._isRequestToMlh(url):
        return this._overrideMlhHost(url, appUrl, environment.mlh_host);
      case this._isRequestToSS(url):
        return this._overrideSSHost(url, appUrl);
      case this._isRequestToCmsImg(url):
        return this._overrideAssetsHost(url);
      default:
        return url;
    }
  }

  /**
   * Returns hostname on which app running
   *
   * @private
   */
  private _getAppHost(): string {
    return this._platform.isBrowser
      ? window.location.host
      : this._injector.get(REQUEST).headers.host;
  }

  public overrideCmsImgUrl(url: string, appUrl: string = `https://${this._getAppHost()}`): string {
    switch (true) {
      case this._isIgnoredHostSS(appUrl):
        return url;
      case this._isRequestToCmsImg(url):
        return this._overrideAssetsHost(url);
      default:
        return url;
    }
  }

  private _overrideAssetsHost(url: string): string {
    const hostPrefix = environment.production ? 'www.' : 'stage.';
    const hostname = this.getDomainName().replace(hostPrefix, '');
    return url.replace(/\bspinpanda\d*\.com\b/g, hostname);
  }

  private _isRequestToCmsImg(url: string): boolean {
    return url.startsWith(environment.assets_host);
  }

  /**
   * Override request to SS
   *
   * @private
   */
  private _overrideSSHost(url: string, host: string): string {
    return url.replace(environment.ss_host, host).replace('https://www.', 'https://').replace('https://stage.', 'https://');
  }

  /**
   * Override request to cms
   *
   * @private
   */
  private _overrideCmsHost(url: string, host: string): string {
    return url.replace(environment.cms_host, host).replace('https://www.', 'https://api.').replace('https://stage.', 'https://stage-api.');
  }


  /**
   * Override request to mlh
   *
   * @private
   */
  private _overrideMlhHost(url: string, host: string, mlhHost: string): string {
    return url.replace(mlhHost, host).replace('https://www.', 'https://mlh.');
  }

  /**
   * Returns true if request url is CMS url
   *
   * @param url
   * @private
   */
  private _isRequestToCms(url: string): boolean {
    return url.startsWith(environment.cms_host);
  }

  /**
   * Returns true if request url is mlh url
   *
   * @param url
   * @private
   */
  private _isRequestToMlh(url: string): boolean {
    return url.startsWith(environment.mlh_host);
  }

  /**
   * Returns true if request url is SS url
   *
   * @param url
   * @private
   */
  private _isRequestToSS(url: string): boolean {
    return url.startsWith(environment.ss_host);
  }

  /**
   * Returns true if url should not be override for SS
   *
   * @param host
   * @private
   */
  private _isIgnoredHostSS(host: string): boolean {
    if (host.includes('localhost') || host.includes('127.0.0.1')) {
      return true;
    }
    return ignoreHosts.some(h => host.includes(h));
  }

  /**
   * Returns true if url should not be override for CMS
   *
   * @param host
   * @private
   */
  private _isIgnoredHostCMS(host: string): boolean {
    if (host.includes('localhost') || host.includes('127.0.0.1')) {
      return true;
    }
    return ignoreHosts.some(h => host.includes(h));
  }

}
